import React, { useEffect, useState } from 'react'
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import HourglassTopIcon from '@mui/icons-material/HourglassTop'
import RateReviewIcon from '@mui/icons-material/RateReview'
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  LinearProgress,
  Paper,
} from '@mui/material'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import API from '../../../../services/API'
import moment from 'moment'
import { useNavigate } from 'react-router-dom'

const staffNextStep = (appraisal) =>
  !appraisal.preAppraisal?.completedAt
    ? 'Pre-appraisal'
    : 'Self-review'

const AppraisalCard = ({ appraisal, onOpen }) => {
  const user = appraisal.employee?.userAccount ?? {}
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ')
  const awaitingStaff = appraisal.status === 'Awaiting Employee'

  return (
    <Grid item xs={12} md={6} xl={4}>
      <Card
        variant="outlined"
        sx={{
          height: '100%',
          borderRadius: 2,
          transition: 'border-color 160ms ease, box-shadow 160ms ease',
          '&:hover': {
            borderColor: 'primary.main',
            boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
          },
        }}
      >
        <CardContent sx={{ height: '100%' }}>
          <Stack gap={2}>
            <Stack direction="row" gap={1.5} alignItems="center">
              <Avatar src={user.photograph} sx={{ width: 48, height: 48 }}>
                {name?.[0]}
              </Avatar>
              <Box minWidth={0}>
                <Typography fontWeight={700} noWrap>
                  {name || 'Employee'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {appraisal.employee?.clinicalRole || 'Clinical staff'}
                </Typography>
              </Box>
            </Stack>

            <Divider />

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              gap={1.5}
              justifyContent="space-between"
            >
              <Stack direction="row" gap={1} alignItems="center">
                <CalendarTodayIcon fontSize="small" color="action" />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Due date
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {appraisal.dueDate
                      ? moment(appraisal.dueDate).format('Do MMM YYYY')
                      : 'Not set'}
                  </Typography>
                </Box>
              </Stack>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {awaitingStaff ? 'Waiting on' : 'Ready since'}
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {awaitingStaff
                    ? staffNextStep(appraisal)
                    : appraisal.employeeSection?.completedAt
                      ? moment(appraisal.employeeSection.completedAt).fromNow()
                      : 'Recently'}
                </Typography>
              </Box>
            </Stack>

            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              gap={1}
            >
              <Chip
                size="small"
                color={awaitingStaff ? 'warning' : 'info'}
                label={awaitingStaff ? 'Waiting for Staff' : 'Awaiting HR Review'}
                sx={{ borderRadius: 1 }}
              />
              <Button
                size="small"
                variant={awaitingStaff ? 'outlined' : 'contained'}
                startIcon={awaitingStaff ? undefined : <RateReviewIcon />}
                onClick={onOpen}
              >
                {awaitingStaff ? 'View' : 'Review'}
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Grid>
  )
}

const AppraisalsPendingReview = () => {
  const [appraisals, setAppraisals] = useState([])
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  useEffect(() => {
    API.get('/appraisals/pending-review')
      .then((res) => res.status === 200 && setAppraisals(res.data))
      .finally(() => setLoading(false))
  }, [])

  const openAppraisal = (appraisal) => {
    navigate(
      `/employees/${appraisal.employee?.userAccount?._id}?tab=appraisals`,
    )
  }

  const waitingForStaff = appraisals.filter(
    (a) => a.status === 'Awaiting Employee',
  )
  const awaitingReview = appraisals.filter(
    (a) => a.status === 'Awaiting HR Review',
  )

  return (
    <Stack padding={2} gap={2.5}>
      <Paper
        variant="outlined"
        sx={{
          p: { xs: 2, md: 2.5 },
          borderRadius: 2,
          background:
            'linear-gradient(135deg, rgba(13, 71, 161, 0.06), rgba(46, 125, 50, 0.05))',
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          gap={2}
        >
          <Stack gap={0.75}>
            <Stack direction="row" gap={1} alignItems="center">
              <RateReviewIcon color="primary" />
              <Typography variant="h6" fontWeight={700}>
                Open Appraisals
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" maxWidth={720}>
              Appraisals still with the staff member are listed first — they
              become reviewable once the pre-appraisal and self-review are
              complete. Open a record from the review queue to complete the HR
              review and agree any follow-up actions.
            </Typography>
          </Stack>
          <Stack
            direction="row"
            gap={1}
            sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}
          >
            <Chip
              color={waitingForStaff.length ? 'warning' : 'success'}
              icon={<HourglassTopIcon />}
              label={`${waitingForStaff.length} waiting for staff`}
            />
            <Chip
              color={awaitingReview.length ? 'info' : 'success'}
              icon={<AssignmentTurnedInIcon />}
              label={`${awaitingReview.length} to review`}
            />
          </Stack>
        </Stack>
      </Paper>

      {loading && <LinearProgress />}

      {!loading && appraisals.length === 0 && (
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
          <Typography fontWeight={600}>All caught up</Typography>
          <Typography variant="body2" color="text.secondary">
            There are no open appraisals right now.
          </Typography>
        </Paper>
      )}

      {!loading && waitingForStaff.length > 0 && (
        <Stack gap={1}>
          <Typography variant="subtitle1" fontWeight={700}>
            Waiting for Staff
          </Typography>
          <Grid container spacing={2}>
            {waitingForStaff.map((a) => (
              <AppraisalCard
                key={a._id}
                appraisal={a}
                onOpen={() => openAppraisal(a)}
              />
            ))}
          </Grid>
        </Stack>
      )}

      {!loading && awaitingReview.length > 0 && (
        <Stack gap={1}>
          <Typography variant="subtitle1" fontWeight={700}>
            Awaiting HR Review
          </Typography>
          <Grid container spacing={2}>
            {awaitingReview.map((a) => (
              <AppraisalCard
                key={a._id}
                appraisal={a}
                onOpen={() => openAppraisal(a)}
              />
            ))}
          </Grid>
        </Stack>
      )}
    </Stack>
  )
}

export default AppraisalsPendingReview
